"use client";

import { Menu } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { usePathname, useRouter } from "next/navigation";

import { Icon } from "../../icons";
import { cn } from "../../utils/cn";

type LanguageSwitcherProps = {
  locale: string;
  locales: readonly string[];
  labels: Record<string, string>;
  collapsed?: boolean;
};

// Since next-intl 4.13.3 its middleware writes NEXT_LOCALE only on document
// requests, so a client-side switch writes it here: next-intl's default locale
// cookie, which both apps use. Not next-intl/navigation's router: Playwright
// loads this barrel in Node, where its ESM build cannot resolve next/navigation.
function saveLocaleCookie(locale: string) {
  document.cookie = `NEXT_LOCALE=${locale}; path=/; samesite=lax`;
}

export function LanguageSwitcher({
  locale,
  locales,
  labels,
  collapsed = false,
}: LanguageSwitcherProps) {
  const router = useRouter();
  const pathname = usePathname();
  // Below lg the sidebar renders inside the 280px mobile drawer, where a
  // right-positioned dropdown lands outside the viewport (GD-42).
  const isMobile = useMediaQuery("(max-width: 1023px)");

  function handleChange(newLocale: string) {
    if (newLocale === locale) return;
    saveLocaleCookie(newLocale);
    const segments = pathname.split("/");
    segments[1] = newLocale;
    router.push(segments.join("/"));
  }

  return (
    <Menu
      shadow="lg"
      width={180}
      radius="lg"
      position={isMobile ? "top-start" : "right-end"}
      id="language-switcher-menu"
    >
      <Menu.Target>
        <button
          data-testid="language-switcher"
          className={cn(
            "flex w-full items-center rounded-xl text-[14px] font-medium",
            "text-sidebar-text transition-all duration-shell ease-shell",
            "hover:bg-pureWhite/10 hover:text-sidebar-text-hover",
            collapsed ? "justify-center px-2.5 py-2.5" : "gap-3 px-3 py-2.5",
          )}
          title={collapsed ? (labels[locale] ?? locale) : undefined}
        >
          <Icon name="globe" size={18} />
          {!collapsed && <span>{labels[locale] ?? locale}</span>}
        </button>
      </Menu.Target>

      <Menu.Dropdown className="border-0 p-1">
        {locales.map((loc) => (
          <Menu.Item
            key={loc}
            data-testid={`language-option-${loc}`}
            onClick={() => handleChange(loc)}
            className={cn(
              "rounded-lg text-base",
              loc === locale
                ? "font-semibold text-primary"
                : "text-text-secondary",
            )}
          >
            {labels[loc] ?? loc}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
